import type { AuthUser } from '../types';

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string');
}

/**
 * Normalizes /auth/me and /auth/login payloads after envelope unwrap.
 * Guards against missing roles/permissions when the API or cache returns a partial user.
 */
export function normalizeAuthUser(payload: unknown): AuthUser | null {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const record = payload as Record<string, unknown>;

  if (record['success'] === true && 'data' in record) {
    return normalizeAuthUser(record['data']);
  }

  const id = record['id'];
  const email = record['email'];
  if (typeof id !== 'string' || typeof email !== 'string') {
    return null;
  }

  const tenantId = record['tenantId'] ?? record['tenant_id'];
  const emailVerifiedAt = record['emailVerifiedAt'] ?? record['email_verified_at'];
  const createdAt = record['createdAt'] ?? record['created_at'];
  const fullName = record['fullName'] ?? record['full_name'];
  const profileCompleted = record['profileCompleted'] ?? record['profile_completed'];
  const requiresProfileCompletion =
    record['requiresProfileCompletion'] ?? record['requires_profile_completion'];
  const missingFields = record['missingFields'] ?? record['missing_fields'];

  return {
    id,
    email,
    fullName: typeof fullName === 'string' ? fullName : '',
    locale: typeof record['locale'] === 'string' ? record['locale'] : 'ar',
    roles: asStringArray(record['roles']),
    permissions: asStringArray(record['permissions']),
    tenantId: typeof tenantId === 'string' ? tenantId : tenantId === null ? null : null,
    emailVerifiedAt:
      typeof emailVerifiedAt === 'string'
        ? emailVerifiedAt
        : emailVerifiedAt instanceof Date
          ? emailVerifiedAt.toISOString()
          : null,
    isActive: record['isActive'] !== false && record['is_active'] !== false,
    createdAt:
      typeof createdAt === 'string'
        ? createdAt
        : createdAt instanceof Date
          ? createdAt.toISOString()
          : new Date().toISOString(),
    profileCompleted: profileCompleted === true,
    requiresProfileCompletion: requiresProfileCompletion === true,
    missingFields: asStringArray(missingFields),
  };
}
